<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="handleOverlayClick">
      <div class="modal-container" :style="{ maxWidth: maxWidth }">
        <div v-if="title || showCloseButton" class="modal-header">
          <h3 v-if="title" class="modal-title">{{ title }}</h3>
          <button 
            v-if="showCloseButton" 
            @click="close" 
            class="modal-close-btn"
          >
            &times;
          </button>
        </div>
        <div class="modal-body">
          <slot name="body">
            <p v-if="message" class="modal-message">{{ message }}</p>
          </slot>
        </div>
        <div v-if="buttons && buttons.length > 0" class="modal-footer">
          <button
            v-for="(button, index) in buttons"
            :key="index"
            @click="handleButtonClick(button)"
            :class="['modal-btn', `modal-btn-${button.type || 'primary'}`]"
          >
            {{ button.label }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { defineProps, defineEmits, defineOptions } from 'vue'

defineOptions({
  name: 'BaseModal'
})

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    default: ''
  },
  showCloseButton: {
    type: Boolean,
    default: true
  },
  closeOnOverlayClick: {
    type: Boolean,
    default: true
  },
  buttons: {
    type: Array,
    default: () => []
  },
  maxWidth: {
    type: String,
    default: '400px'
  }
})

const emit = defineEmits(['update:show', 'close', 'button-click'])

const close = () => {
  emit('update:show', false)
  emit('close')
}

const handleOverlayClick = () => {
  if (props.closeOnOverlayClick) {
    close()
  }
}

const handleButtonClick = (button) => {
  if (button.onClick) {
    button.onClick()
  } else {
    emit('button-click', button)
  }
  if (button.close !== false) {
    close()
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
  backdrop-filter: blur(2px);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-container {
  background: #ffffff;
  border-radius: 12px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
  overflow: hidden;
}

@keyframes slideUp {
  from {
    transform: translateY(0) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E0E0E0;
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  color: rgb(0, 0, 0);
  margin: 0;
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  background: #f5f5f5;
  color: #000;
}

.modal-body {
  padding: 24px;
}

.modal-message {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E0E0E0;
  justify-content: center;
}

.modal-btn {
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-btn-primary {
  background: #000000;
  color: #ffffff;
}

.modal-btn-primary:hover {
  background: #333333;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-btn-secondary {
  background: #f5f5f5;
  color: #666;
}

.modal-btn-secondary:hover {
  background: #e0e0e0;
  color: #000;
}
</style>
